/**
 * Zustand store that holds the current auth state (user + trial +
 * subscription) and the hydration lifecycle.
 *
 *   1. App boot: ``main.tsx`` calls ``useAuth.getState().hydrate()``
 *      once. We fetch ``/api/auth/me`` and write the result into the
 *      store, regardless of whether the user is authenticated. The
 *      ``status`` field flips ``loading → ready`` once the call
 *      completes (success OR failure). Route guards block until then.
 *   2. ``logout()`` posts ``/api/auth/logout`` (best-effort) and
 *      clears the store.
 *   3. ``setMe()`` is called by the Login page after the TG widget
 *      callback succeeds — we re-fetch ``/api/auth/me`` rather than
 *      trust the callback's body, so the role / trial / subscription
 *      come from a single source of truth.
 *
 * NOT persisted: HttpOnly cookies are the source of truth for the
 * session — rehydrating from localStorage would only mask cookie
 * expiry. Each page load asks the server.
 */
import { create } from "zustand";

import { api } from "../lib/api";
import type {
  AuthProvider,
  AuthUser,
  MeResponse,
  Subscription,
  Trial,
} from "../types/auth";

type AuthStatus = "loading" | "ready";

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  trial: Trial | null;
  subscription: Subscription | null;
  linkedProviders: AuthProvider[];
  /** Last error message from ``hydrate()`` — surfaced on Login page
   *  so a flaky network or 5xx is visible instead of "stuck loading". */
  error: string | null;

  hydrate: () => Promise<void>;
  setMe: (me: MeResponse) => void;
  logout: () => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  status: "loading",
  user: null,
  trial: null,
  subscription: null,
  linkedProviders: [],
  error: null,

  hydrate: async () => {
    try {
      const me = await api.get<MeResponse>("/api/auth/me");
      set({
        status: "ready",
        user: me.user,
        trial: me.trial,
        subscription: me.subscription,
        linkedProviders: me.linked_providers ?? [],
        error: null,
      });
    } catch (err) {
      // /me is designed never to 401 (returns user:null for anon), so
      // any failure here is a network / 5xx. Mark ready so guards can
      // redirect to /login instead of hanging.
      const msg = err instanceof Error ? err.message : "Unknown error";
      set({
        status: "ready",
        user: null,
        trial: null,
        subscription: null,
        linkedProviders: [],
        error: msg,
      });
    }
  },

  setMe: (me) =>
    set({
      status: "ready",
      user: me.user,
      trial: me.trial,
      subscription: me.subscription,
      linkedProviders: me.linked_providers ?? [],
      error: null,
    }),

  logout: async () => {
    try {
      await api.post("/api/auth/logout", {});
    } catch {
      // Cookie may already be invalid; clear local state regardless.
    }
    set({
      status: "ready",
      user: null,
      trial: null,
      subscription: null,
      linkedProviders: [],
      error: null,
    });
  },
}));

// ---------------------------------------------------------------------------
// Convenience selectors — pre-defined so components can use them with
// ``useAuth(selectIsAdmin)`` and benefit from referential stability
// (re-render only when the selected slice changes).
// ---------------------------------------------------------------------------

export const selectUser = (s: AuthState) => s.user;
export const selectStatus = (s: AuthState) => s.status;
export const selectIsAuthed = (s: AuthState) => s.user !== null;
export const selectIsAdmin = (s: AuthState) => s.user?.role === "admin";
